<template>
    <section class="flex flex-col h-full relative">
      <header class="fixed max-w-md w-full top-0 z-10 bg-light py-4 px-6 flex items-center justify-between border-b border-border-200 border-opacity-75">
        <div class="flex text-primary-dark font-semibold">
          <CartCheckBag class="flex-shrink-0" :width="24" :height="22" />
          <span class="flex ms-2">
            {{ formatString(cart.totalUniqueItems, $t("common:text-item")) }}
          </span>
        </div>
        <button
          class="w-7 h-7 ms-3 -me-2 flex items-center justify-center rounded-full text-muted bg-gray-100 transition-all duration-200 focus:outline-none hover:bg-accent focus:bg-accent hover:text-light focus:text-light"
          :aria-label="$t('text-close')"
          @click="closeSidebar"
        >
          <span class="sr-only">{{ $t("text-close") }}</span>
          <CloseIcon class="w-3 h-3" />
        </button>
      </header>

      <div v-if="cart.loading" class="grid place-items-center h-full">
          <Spinner :text="$t('common:text-loading')" />
      </div>

      <div v-else class="flex-grow pt-16" :style="{ height: 'calc(100vh - 85px)' }">
        <div v-if="cart.list.items.length > 0">
          <div v-if="visibleCarts.length" class="border-b border-solid border-border-200 border-opacity-75 p-5 text-xs lg:text-sm">
            <p
              v-for="(line, idx) in (visibleCarts.length === 1 ? $t('text-one-active-cart-hint-title') : $t('text-more-active-carts-hint-title')).split(/(?<=\.)/)"
              :key="idx"
            >
              {{ line }}
            </p>
          </div>
          <div
            v-for="group in cart.list.items"
            :key="group.id"
            @click="() => handleCheckoutCart(group.id)"
          >
            <div
              v-if="group.variants.length > 0"
              class="border-b border-solid border-border-200 border-opacity-75 hover:bg-gray-100 pt-8"
            >
              <div
                class="flex flex-row items-center justify-between space-x-2 ml-3 mr-4 cursor-pointer focus:outline-none focus:bg-accent focus:text-light rounded"
              >
                <ProfileHeader :data="group" class="m-2 overflow-hidden text-primary-dark" :type="CART_TYPE.SELLER" :show="{ rating: true, status: true }" />
              </div>
              <CartItem v-for="item in group.variants" :key="item.id" :data="item" :currency="group.currency" />
              <div class="text-center mb-9">
                <Button class="ms-auto" variant="submit" size="small">
                  <div class="flex items-center space-x-2 text-sm">
                    <div>{{ $t('text-proceed-order') }}</div>
                    <ChevronRight class="w-4 h-4" />
                  </div>
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div
            v-else
            layout
            initial="from"
            animate="to"
            exit="from"
            variants="fadeInOut(0.25)"
            class="h-full flex flex-col items-center justify-center"
          >
            <CartCheckBag class="w-40 h-40 text-primary-dark" />
            <div class="mt-6 text-base font-semibold text-bolder">
              {{ $t("text-no-products") }}
            </div>
          </div>
      </div>
    </section>
</template>

<script setup>
import _ from 'lodash-es';
import { ROUTES } from "@utils/routes";
import { CART_TYPE } from "@utils/constants";
import { formatString } from "@utils/format-string";
import { useCartStore } from '@stores/cart';
import Spinner from "@components/ui/loaders/spinner/spinner";
import CartItem from "@components/cart/cart-item";
import CartCheckBag from "@components/icons/cart-check-bag";
import CloseIcon from "@components/icons/close-icon";
import ProfileHeader from "@components/profile/profile-header";
import Button from "@components/ui/button";
import ChevronRight from "@components/icons/chevron-right";

const cart = useCartStore();
const { $eventBus } = useNuxtApp();

const closeSidebar = () => {
    $eventBus.emit('sidebar:close');
}

const handleCheckoutCart = async (id) => {
    closeSidebar();
    await navigatorTo(translatePath(ROUTES.CODE.CHECKOUT, `/${id}`));
}

const visibleCarts = computed(() => _.filter(cart.list.items, group => group.variants.length > 0));

</script>
